import type { MutationOutputItem as PrimaryActionType } from '@confluence/page-selection-table';
import type { ItemId, TreeObjectItems } from '@confluence/tree';
import { Attribution } from '@confluence/error-boundary';
import { getMonitoringClient } from '@confluence/monitoring';

import type { ContentTreeItem } from '../data-transformers';

/**
 * Below is the exported type with comments next to it for context because the variable names are not very clear.
 *
 * export type MutationOutputItem = {
	pageID: string;
	hasChildren: boolean;
	areChildrenIncluded: boolean; // Are all descendants included?
	excludedBranchRootPageIDs: string[]; // All direct children that are excluded.
	hasRestrictions: boolean; // Current node has restrictions
	hasInheritedRestrictions: boolean; // Within this tree, there are inherited restrictions
	contentType: string | null;

	// These attributes below will have preset values
	hasSelectedAncestor: boolean; // This is for archived pages w| note, which is not required.
	excludedBranchRootPageHasInheritedRestrictions: boolean[]; // We are not addressing this because we are excluding it.
	isSelected: boolean; // This will always be true in our scenario, we always want to include the current tree item.
};
 */

const createPrimaryActionInput = ({
	treeItem,
	areChildrenIncluded,
	excludedBranchRootPageIDs = [],
	hasInheritedRestrictions = false,
}: {
	treeItem: ContentTreeItem;
	areChildrenIncluded: boolean;
	excludedBranchRootPageIDs?: ItemId[];
	hasInheritedRestrictions?: boolean;
}) =>
	({
		pageID: treeItem.id,
		areChildrenIncluded,
		hasRestrictions: treeItem.hasRestrictions ?? false,
		hasInheritedRestrictions: hasInheritedRestrictions ?? false,
		excludedBranchRootPageIDs,
		hasChildren: treeItem.hasChildren,
		contentType: treeItem.data.type ?? null,

		// These attributes are expected in the schema but will have preset values and can be ignored for our use case.
		isSelected: true,
		excludedBranchRootPageHasInheritedRestrictions: [],
		hasSelectedAncestor: false,
	}) as PrimaryActionType;

// Primary actions include archive and delete
export const transformToPrimaryActionInput = (
	root: ContentTreeItem,
	pages: TreeObjectItems<ContentTreeItem>,
	contentIdsWithLoadedChildren: Set<ItemId>,
	selectedContent: Record<
		ItemId,
		{
			selectedDescendantCount: number;
			deselectedChildren: Set<ItemId>;
		}
	>,
): PrimaryActionType[] | Error => {
	// This is a set of contentIds that should be ignored if a parent node has addressed all of its descendants from the areChildrenIncluded argument
	let ignoreSet = new Set<ItemId>();

	// Checking if all descendants are selected and saving the selected descendants in a set to be ignored
	// if and only if all descendants are selected to prevent another recursive traversal against the TreeNodeItem
	const collectSelectedItemsAndStatus = (
		item: ContentTreeItem,
	): { selectedSet: Set<ItemId>; allSelected: boolean; hasInheritedRestrictions: boolean } => {
		const selectedSet = new Set<ItemId>();
		let hasInheritedRestrictions = false;

		const traverse = (currItem: ContentTreeItem): boolean => {
			if (
				selectedContent[currItem.id] === undefined ||
				selectedContent[currItem.id].deselectedChildren.size > 0
			)
				return false;

			const isSelected = selectedContent.hasOwnProperty(currItem.id);
			if (isSelected) {
				selectedSet.add(currItem.id);
				if (currItem.hasRestrictions && currItem.hasChildren) {
					hasInheritedRestrictions = true;
				}
			}

			const children = currItem.children || [];
			let allChildrenSelected = true;

			for (const childId of children) {
				const child = pages[childId];
				if (child.data.status !== 'DRAFT') {
					if (child) {
						const childAllSelected = traverse(child);
						allChildrenSelected = allChildrenSelected && childAllSelected;
					} else {
						// If the child doesn't exist in pages, treat it as not selected
						allChildrenSelected = false;
					}
				}
			}

			// Current node must be selected AND all children must be selected
			return isSelected && allChildrenSelected;
		};

		const allSelected = traverse(item);
		return { selectedSet, allSelected, hasInheritedRestrictions };
	};

	const result: PrimaryActionType[] = [];

	const getSelectedItems = (treeItem: ContentTreeItem) => {
		const isSelected = treeItem.id in selectedContent;
		const hasChildren = treeItem.hasChildren;
		const children = treeItem.children;

		if (!ignoreSet.has(treeItem.id) && treeItem.data.status !== 'DRAFT') {
			// Leaf node is selected
			if (isSelected && !hasChildren) {
				result.push(createPrimaryActionInput({ treeItem, areChildrenIncluded: false }));
			}
			// Parent is selected but not expanded yet
			else if (isSelected && hasChildren && !contentIdsWithLoadedChildren.has(treeItem.id)) {
				result.push(createPrimaryActionInput({ treeItem, areChildrenIncluded: true }));
			}
			// Parent is selected, has children, and some direct children are deselected
			else if (
				isSelected &&
				hasChildren &&
				selectedContent[treeItem.id].deselectedChildren.size > 0
			) {
				result.push(
					createPrimaryActionInput({
						treeItem,
						areChildrenIncluded: false,
						excludedBranchRootPageIDs: [...selectedContent[treeItem.id].deselectedChildren],
					}),
				);
			}
			// Parent is selected, has children, and all descendants are selected
			else if (isSelected && hasChildren) {
				const { selectedSet, allSelected, hasInheritedRestrictions } =
					collectSelectedItemsAndStatus(treeItem);
				if (allSelected) {
					result.push(
						createPrimaryActionInput({
							treeItem,
							areChildrenIncluded: true,
							excludedBranchRootPageIDs: [],
							hasInheritedRestrictions,
						}),
					);
					ignoreSet = new Set([...selectedSet, ...ignoreSet]);
				} else {
					// This scenario should never be reached, but this is a just in case. It should also be covered by the archive/delete mutation when it fails.
					const err = new Error(
						'[Bulk Actions on Content Tree] Error formatting selected items for primary actions (delete or archive)',
					);
					getMonitoringClient().submitError(err, {
						attribution: Attribution.DISCO,
					});

					return err;
				}
			}
		}

		for (const childId of children || []) {
			const child = pages[childId];

			if (child) {
				getSelectedItems(child);
			}
		}

		// Has previously been addressed by parent with the areChildrenIncluded argument
		if (isSelected && ignoreSet.has(treeItem.id)) {
			if (!hasChildren) {
				// TODO @awu address the case where nodes with children have also been accounted for
				delete selectedContent[treeItem.id];
				ignoreSet.delete(treeItem.id);
			}
		}

		return result;
	};

	return getSelectedItems(root);
};
